import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Clock, ShieldCheck, Siren } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import { BRAND, telLink, waLink, monitoringTelLink } from "@/lib/brand";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-cyan/5 via-transparent to-[#0A0A0B]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-10 lg:pt-24">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan text-xs font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" />
              Free security audit · Guwahati
            </div>
            <h1
              data-testid="contact-heading"
              className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]"
            >
              Let's talk about <span className="text-brand-cyan text-glow">your security</span>.
            </h1>
            <p className="mt-5 text-white/70 text-base sm:text-lg max-w-2xl">
              Tell us a little about your property. A Secure Watch specialist will call you within
              30 minutes to schedule a free on-site audit — anywhere in Guwahati.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Split */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-14 lg:py-20 grid gap-10 lg:grid-cols-12">
        {/* Form */}
        <motion.div {...fadeUp} className="lg:col-span-7">
          <LeadForm />
        </motion.div>

        {/* Contact info */}
        <motion.aside {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-5 space-y-5">
          <a
            href={telLink}
            data-testid="contact-call-card"
            className="group block rounded-2xl border border-brand-cyan/40 bg-gradient-to-br from-brand-cyan/15 to-transparent p-6 hover:border-brand-cyan transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-brand-cyan text-black flex items-center justify-center shadow-[0_0_20px_rgba(0,229,255,0.4)]">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-brand-cyan">Fastest way</div>
                <div className="font-heading text-2xl font-bold text-white">{BRAND.phoneDisplay}</div>
                <div className="text-xs text-white/50 mt-1">Available 10 PM – 6 AM · Assamese, Hindi, English</div>
              </div>
            </div>
          </a>

          <a
            href={monitoringTelLink}
            data-testid="contact-monitoring-card"
            className="group block rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-600/15 to-transparent p-6 hover:border-red-400 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-red-600 text-white flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.5)]">
                <Siren className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-red-400 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 pulse-dot" /> Monitoring Room · SOS
                </div>
                <div className="font-heading text-2xl font-bold text-white">{BRAND.monitoringPhoneDisplay}</div>
                <div className="text-xs text-white/50 mt-1">Command room hotline · Active 10 PM – 6 AM</div>
              </div>
            </div>
          </a>

          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="contact-wa-card"
            className="group block rounded-2xl border border-white/10 bg-[#0F0F13] p-6 hover:border-[#25D366]/60 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#25D366] text-black flex items-center justify-center">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#25D366]">WhatsApp</div>
                <div className="font-heading text-xl font-bold text-white">Chat with us instantly</div>
                <div className="text-xs text-white/50 mt-1">Typically replies within 5 minutes</div>
              </div>
            </div>
          </a>

          <a
            href={`mailto:${BRAND.email}`}
            data-testid="contact-email-card"
            className="group block rounded-2xl border border-white/10 bg-[#0F0F13] p-6 hover:border-white/25 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-white/10 text-white flex items-center justify-center">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Email</div>
                <div className="font-heading text-lg font-bold text-white break-all">{BRAND.email}</div>
              </div>
            </div>
          </a>

          <div
            data-testid="contact-address-card"
            className="rounded-2xl border border-white/10 bg-[#0F0F13] p-6"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-white/10 text-brand-cyan flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Command Center</div>
                <div className="font-heading text-base font-bold text-white mt-1">
                  Secure Watch — Guwahati HQ
                </div>
                <p className="text-sm text-white/60 mt-1 leading-relaxed">{BRAND.address}</p>
                <a
                  href={BRAND.mapsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-xs text-brand-cyan mt-3"
                  data-testid="contact-map-link"
                >
                  Get directions on Google Maps →
                </a>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0F0F13] p-6">
            <div className="flex items-center gap-3 text-white">
              <Clock className="h-5 w-5 text-brand-cyan" />
              <div className="font-heading font-bold">Command center hours</div>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-white/70">
              <li className="flex justify-between"><span>Live monitoring</span><span className="text-brand-cyan font-semibold">10 PM – 6 AM · every night</span></li>
              <li className="flex justify-between"><span>Office visits</span><span>Mon – Sat · 10 AM – 7 PM</span></li>
              <li className="flex justify-between"><span>Sunday</span><span>On-call only</span></li>
            </ul>
          </div>
        </motion.aside>
      </section>

      {/* Map embed */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24">
        <div className="rounded-2xl overflow-hidden border border-white/10">
          <iframe
            title="Secure Watch Guwahati location"
            data-testid="contact-map-embed"
            src={BRAND.mapsEmbed}
            width="100%"
            height="420"
            loading="lazy"
            allowFullScreen=""
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0, filter: "grayscale(0.4) invert(0.85) hue-rotate(180deg)" }}
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
